/**
 * UsersTab - Admin User Management
 *
 * Displays all users with their subscription info, secondary accounts,
 * and allows blocking/unblocking and deleting users.
 * Secondary users are shown nested under their owner account.
 * 
 * Responsive: Shows cards on mobile, table on desktop.
 */

import { useState, useEffect, useCallback, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card } from '../../components/ui';
import api from '../../services/api';
import { PendingDeletionsSection } from './PendingDeletionsSection';

interface AdminUserView {
  id: string;
  email: string;
  role: 'free' | 'premium' | 'admin';
  emailVerified: boolean;
  twoFactorEnabled: boolean;
  language: 'fr' | 'en';
  createdAt: string;
  lastLogin?: string;
  isSuspended: boolean;
  subscriptionStatus?: string;
  operationCount: number;
  accountCount: number;
  secondaryUserCount: number;
  isSecondaryUser: boolean;
  ownerEmail?: string;
  ownerId?: string;
  ownerIsSuspended?: boolean;
  deletionRequestedAt?: string;
  deletionScheduledAt?: string;
}

interface PaginatedAdminUsers {
  items: AdminUserView[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: { code: string; message: string };
}

export function UsersTab() {
  const { t } = useTranslation();
  const [users, setUsers] = useState<AdminUserView[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize] = useState(20);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [suspendedFilter, setSuspendedFilter] = useState<string>('');
  
  // Action states
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [confirmRoleChange, setConfirmRoleChange] = useState<AdminUserView | null>(null);
  
  // Payments modal
  const [viewPaymentsUser, setViewPaymentsUser] = useState<AdminUserView | null>(null);
  const [paymentsData, setPaymentsData] = useState<{
    payments: Array<{
      id: string;
      orderId: string;
      amount: number;
      currency: string;
      paymentType: string;
      paymentTypeLabel: string;
      status: string;
      statusLabel: string;
      createdAt: string;
    }>;
    totalPayments: number;
    totalPaid: number;
  } | null>(null);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  
  // Expanded rows (to show secondary users)
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // Load users
  const loadUsers = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('pageSize', pageSize.toString());
      if (searchTerm) params.append('search', searchTerm);
      if (roleFilter) params.append('role', roleFilter);
      if (suspendedFilter) params.append('isSuspended', suspendedFilter);

      const response = await api.get<ApiResponse<PaginatedAdminUsers>>(`/admin/users?${params}`);
      if (response.data.success && response.data.data) {
        setUsers(response.data.data.items);
        setTotal(response.data.data.total);
        setTotalPages(response.data.data.totalPages);
      } else {
        setError(response.data.error?.message || t('admin.users.loadError'));
      }
    } catch {
      setError(t('admin.users.loadError'));
    } finally {
      setIsLoading(false);
    }
  }, [page, pageSize, searchTerm, roleFilter, suspendedFilter, t]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // Handle search with debounce
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setPage(1);
  };

  // Toggle expanded row
  const toggleExpanded = (userId: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  // Toggle user suspension
  const handleToggleSuspend = async (user: AdminUserView) => {
    setActionLoading(user.id);
    try {
      const endpoint = user.isSuspended 
        ? `/admin/users/${user.id}/unsuspend`
        : `/admin/users/${user.id}/suspend`;
      
      const response = await api.put<ApiResponse<null>>(endpoint);
      if (response.data.success) {
        await loadUsers();
      } else {
        setError(response.data.error?.message || t('admin.users.actionError'));
      }
    } catch {
      setError(t('admin.users.actionError'));
    } finally {
      setActionLoading(null);
    }
  };

  // Reactivate user (cancel pending deletion)
  const handleReactivate = async (user: AdminUserView) => {
    setActionLoading(user.id);
    try {
      const response = await api.post<ApiResponse<null>>(`/admin/users/${user.id}/reactivate`);
      if (response.data.success) {
        await loadUsers();
      } else {
        setError(response.data.error?.message || t('admin.users.actionError'));
      }
    } catch {
      setError(t('admin.users.actionError'));
    } finally {
      setActionLoading(null);
    }
  };

  // Toggle user subscription (free <-> premium) - requires confirmation
  const handleToggleSubscription = async (user: AdminUserView) => {
    if (user.role === 'admin') return; // Cannot change admin role
    setConfirmRoleChange(user);
  };

  // Confirm and execute role change
  const confirmToggleSubscription = async () => {
    if (!confirmRoleChange) return;
    
    const user = confirmRoleChange;
    setActionLoading(user.id);
    setConfirmRoleChange(null);
    
    try {
      const newRole = user.role === 'premium' ? 'free' : 'premium';
      const response = await api.put<ApiResponse<{ role: string }>>(`/admin/users/${user.id}/subscription`, {
        role: newRole,
      });
      if (response.data.success) {
        await loadUsers();
      } else {
        setError(response.data.error?.message || t('admin.users.actionError'));
      }
    } catch {
      setError(t('admin.users.actionError'));
    } finally {
      setActionLoading(null);
    }
  };

  // Load user payments
  const loadUserPayments = async (user: AdminUserView) => {
    setViewPaymentsUser(user);
    setPaymentsLoading(true);
    setPaymentsData(null);
    try {
      const response = await api.get<ApiResponse<{
        payments: Array<{
          id: string;
          orderId: string;
          amount: number;
          currency: string;
          paymentType: string;
          paymentTypeLabel: string;
          status: string;
          statusLabel: string;
          createdAt: string;
        }>;
        totalPayments: number;
        totalPaid: number;
      }>>(`/admin/users/${user.id}/payments`);
      if (response.data.success && response.data.data) {
        setPaymentsData(response.data.data);
      }
    } catch {
      setError(t('admin.users.actionError'));
    } finally {
      setPaymentsLoading(false);
    }
  };

  // Delete user
  const handleDelete = async (userId: string) => {
    setActionLoading(userId);
    try {
      const response = await api.delete<ApiResponse<null>>(`/admin/users/${userId}`);
      if (response.data.success) {
        setConfirmDelete(null);
        await loadUsers();
      } else {
        setError(response.data.error?.message || t('admin.users.deleteError'));
      }
    } catch {
      setError(t('admin.users.deleteError'));
    } finally {
      setActionLoading(null);
    }
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  // Get role badge style
  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'admin':
        return 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300';
      case 'premium':
        return 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300';
      default:
        return 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300';
    }
  };

  // Separate primary and secondary users
  const primaryUsers = users.filter(u => !u.isSecondaryUser);
  const secondaryUsersByOwner = users
    .filter(u => u.isSecondaryUser)
    .reduce((acc, user) => {
      const ownerId = user.ownerId;
      if (ownerId) {
        if (!acc[ownerId]) acc[ownerId] = [];
        acc[ownerId].push(user);
      }
      return acc;
    }, {} as Record<string, AdminUserView[]>);

  // Render action buttons for a user
  const renderActions = (user: AdminUserView, isSecondary = false) => (
    <div className="flex justify-end gap-2">
      {/* Block/Unblock/Reactivate button - not for admin users */}
      {user.role !== 'admin' && !isSecondary && (
        user.deletionRequestedAt ? (
          // User has pending deletion - show reactivate button
          <button
            onClick={() => handleReactivate(user)}
            disabled={actionLoading === user.id}
            className="p-2 rounded-lg transition-colors text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/30 disabled:opacity-50"
            title={t('admin.pendingDeletions.reactivate')}
          >
            {actionLoading === user.id ? (
              <LoadingIcon className="w-5 h-5 animate-spin" />
            ) : (
              <RefreshIcon className="w-5 h-5" />
            )}
          </button>
        ) : (
          // Normal suspend/unsuspend button
          <button
            onClick={() => handleToggleSuspend(user)}
            disabled={actionLoading === user.id}
            className={`p-2 rounded-lg transition-colors ${
              user.isSuspended
                ? 'text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/30'
                : 'text-orange-600 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/30'
            } disabled:opacity-50`}
            title={user.isSuspended ? t('admin.users.unblock') : t('admin.users.block')}
          >
            {actionLoading === user.id ? (
              <LoadingIcon className="w-5 h-5 animate-spin" />
            ) : user.isSuspended ? (
              <UnlockIcon className="w-5 h-5" />
            ) : (
              <LockIcon className="w-5 h-5" />
            )}
          </button>
        )
      )}

      {/* Delete button - not for admin users */}
      {user.role !== 'admin' && (
        <>
          {/* View payments button */}
          <button
            onClick={() => loadUserPayments(user)}
            className="p-2 text-violet-600 dark:text-violet-400 hover:bg-violet-50 dark:hover:bg-violet-900/30 rounded-lg transition-colors"
            title={t('admin.users.viewPayments')}
          >
            <CreditCardIcon className="w-5 h-5" />
          </button>
          {confirmDelete === user.id ? (
            <div className="flex items-center gap-1">
              <button
                onClick={() => handleDelete(user.id)}
                disabled={actionLoading === user.id}
                className="px-2 py-1 text-xs font-medium text-white bg-red-600 rounded hover:bg-red-700 disabled:opacity-50"
              >
                {actionLoading === user.id ? '...' : t('common.confirm')}
              </button>
              <button
                onClick={() => setConfirmDelete(null)}
                className="px-2 py-1 text-xs font-medium text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 rounded hover:bg-slate-200 dark:hover:bg-slate-600"
              >
                {t('common.cancel')}
              </button>
            </div>
          ) : (
            <button
              onClick={() => setConfirmDelete(user.id)}
              className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
              title={t('admin.users.delete')}
            >
              <TrashIcon className="w-5 h-5" />
            </button>
          )}
        </>
      )}
    </div>
  );

  if (isLoading && users.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Error message */}
      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400">
          {error}
          <button onClick={() => setError(null)} className="ml-2 underline">
            {t('common.close')}
          </button>
        </div>
      )}

      {/* Pending deletions section */}
      <PendingDeletionsSection />

      {/* Filters */}
      <Card>
        <div className="p-4">
          <div className="space-y-4 md:space-y-0 md:grid md:grid-cols-4 md:gap-4">
            {/* Search */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
                {t('admin.users.search')}
              </label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder={t('admin.users.searchPlaceholder')}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
              />
            </div>

            {/* Role filter */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
                {t('admin.users.role')}
              </label>
              <select
                value={roleFilter}
                onChange={(e) => { setRoleFilter(e.target.value); setPage(1); }}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
              >
                <option value="">{t('admin.users.allRoles')}</option>
                <option value="admin">{t('roles.admin')}</option>
                <option value="premium">{t('roles.premium')}</option>
                <option value="free">{t('roles.free')}</option>
              </select>
            </div>

            {/* Status filter */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
                {t('admin.users.status')}
              </label>
              <select
                value={suspendedFilter}
                onChange={(e) => { setSuspendedFilter(e.target.value); setPage(1); }}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
              >
                <option value="">{t('admin.users.allStatuses')}</option>
                <option value="false">{t('admin.users.active')}</option>
                <option value="true">{t('admin.users.suspended')}</option>
              </select>
            </div>
          </div>
        </div>
      </Card>

      {/* Mobile: User Cards */}
      <div className="md:hidden space-y-3">
        {primaryUsers.map((user) => {
          const linkedUsers = secondaryUsersByOwner[user.id] || [];
          const hasLinkedUsers = linkedUsers.length > 0;
          const isExpanded = expandedRows.has(user.id);

          return (
            <div key={user.id} className="space-y-2">
              {/* Primary user card */}
              <Card className={user.isSuspended ? 'border-red-200 bg-red-50' : ''}>
                <div className="p-4 space-y-3">
                  {/* Email - full width */}
                  <div className="flex items-start gap-2">
                    {hasLinkedUsers && (
                      <button
                        onClick={() => toggleExpanded(user.id)}
                        className="p-1 -ml-1 -mt-0.5 hover:bg-slate-100 rounded transition-colors flex-shrink-0"
                      >
                        <ChevronIcon 
                          className={`w-4 h-4 text-slate-500 transition-transform ${isExpanded ? 'rotate-90' : ''}`} 
                        />
                      </button>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 break-all">{user.email}</p>
                      {!user.emailVerified && (
                        <span className="text-xs text-orange-600">
                          {t('admin.users.emailNotVerified')}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Badges row */}
                  <div className="flex flex-wrap items-center gap-2">
                    {user.role === 'admin' ? (
                      <span className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-full ${getRoleBadge(user.role)}`}>
                        {t(`roles.${user.role}`)}
                      </span>
                    ) : (
                      <button
                        onClick={() => handleToggleSubscription(user)}
                        disabled={actionLoading === user.id}
                        className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full cursor-pointer hover:ring-2 hover:ring-offset-1 hover:ring-violet-400 transition-all ${getRoleBadge(user.role)} disabled:opacity-50`}
                        title={t('admin.users.toggleSubscription')}
                      >
                        {actionLoading === user.id ? (
                          <LoadingIcon className="w-3 h-3 animate-spin" />
                        ) : (
                          <SwitchIcon className="w-3 h-3" />
                        )}
                        {t(`roles.${user.role}`)}
                      </button>
                    )}
                    
                    {user.deletionRequestedAt ? (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400">
                        <ClockIcon className="w-3 h-3" />
                        {t('admin.users.pendingDeletion')}
                      </span>
                    ) : user.isSuspended ? (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full bg-red-100 text-red-700">
                        <BanIcon className="w-3 h-3" />
                        {t('admin.users.suspended')}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">
                        <CheckIcon className="w-3 h-3" />
                        {t('admin.users.active')}
                      </span>
                    )}
                    
                    {hasLinkedUsers && (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-violet-100 text-violet-700 text-xs font-medium rounded-full">
                        <UsersIcon className="w-3 h-3" />
                        {linkedUsers.length} {t('admin.users.linkedAccounts', 'lié(s)')}
                      </span>
                    )}
                  </div>
                  
                  {/* Info row - full width */}
                  <div className="flex items-center text-sm text-slate-600">
                    <span className="font-medium">{user.accountCount}</span>
                    <span className="ml-1">{user.accountCount > 1 ? t('admin.users.accountsPlural', 'comptes bancaires') : t('admin.users.accountSingular', 'compte bancaire')}</span>
                    <span className="mx-2 text-slate-300">•</span>
                    <span>{t('admin.users.createdOn', 'Créé le')} {formatDate(user.createdAt)}</span>
                  </div>
                  
                  {/* Actions - full width at bottom */}
                  {user.role !== 'admin' && (
                    <>
                      <div className="border-t border-slate-200 pt-3">
                        <div className="flex justify-center gap-3">
                          {/* Payments button */}
                          <button
                            onClick={() => loadUserPayments(user)}
                            className="p-3 rounded-lg transition-colors bg-violet-50 text-violet-700 hover:bg-violet-100"
                            title={t('admin.users.viewPayments')}
                          >
                            <CreditCardIcon className="w-5 h-5" />
                          </button>
                          
                          {/* Block/Unblock/Reactivate button */}
                          {user.deletionRequestedAt ? (
                            <button
                              onClick={() => handleReactivate(user)}
                              disabled={actionLoading === user.id}
                              className="p-3 rounded-lg transition-colors bg-green-50 text-green-700 hover:bg-green-100 disabled:opacity-50"
                              title={t('admin.pendingDeletions.reactivate')}
                            >
                              {actionLoading === user.id ? (
                                <LoadingIcon className="w-5 h-5 animate-spin" />
                              ) : (
                                <RefreshIcon className="w-5 h-5" />
                              )}
                            </button>
                          ) : (
                            <button
                              onClick={() => handleToggleSuspend(user)}
                              disabled={actionLoading === user.id}
                              className={`p-3 rounded-lg transition-colors ${
                                user.isSuspended
                                  ? 'bg-green-50 text-green-700 hover:bg-green-100'
                                  : 'bg-orange-50 text-orange-700 hover:bg-orange-100'
                              } disabled:opacity-50`}
                              title={user.isSuspended ? t('admin.users.unblock') : t('admin.users.block')}
                            >
                              {actionLoading === user.id ? (
                                <LoadingIcon className="w-5 h-5 animate-spin" />
                              ) : user.isSuspended ? (
                                <UnlockIcon className="w-5 h-5" />
                              ) : (
                                <LockIcon className="w-5 h-5" />
                              )}
                            </button>
                          )}
                          
                          {/* Delete button */}
                          {confirmDelete === user.id ? (
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleDelete(user.id)}
                                disabled={actionLoading === user.id}
                                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
                              >
                                {actionLoading === user.id ? '...' : t('common.confirm')}
                              </button>
                              <button
                                onClick={() => setConfirmDelete(null)}
                                className="px-4 py-2 text-sm font-medium text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200"
                              >
                                {t('common.cancel')}
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setConfirmDelete(user.id)}
                              className="p-3 rounded-lg transition-colors bg-red-50 text-red-700 hover:bg-red-100"
                              title={t('common.delete')}
                            >
                              <TrashIcon className="w-5 h-5" />
                            </button>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </Card>

              {/* Secondary users (expanded) */}
              {isExpanded && linkedUsers.map((secondary) => (
                <Card key={secondary.id} className="ml-4 border-violet-200 bg-violet-50/50">
                  <div className="p-4 space-y-3">
                    {/* Email - full width */}
                    <div className="flex items-center gap-2">
                      <LinkIcon className="w-4 h-4 text-violet-500 flex-shrink-0" />
                      <p className="text-sm font-medium text-slate-700 break-all">{secondary.email}</p>
                    </div>
                    
                    {/* Badges */}
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="inline-flex px-2.5 py-1 text-xs font-medium rounded-full bg-violet-100 text-violet-700">
                        {t('admin.users.linkedAccount')}
                      </span>
                      
                      {secondary.isSuspended ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full bg-red-100 text-red-700">
                          <BanIcon className="w-3 h-3" />
                          {t('admin.users.suspended')}
                        </span>
                      ) : secondary.ownerIsSuspended ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-700">
                          <BanIcon className="w-3 h-3" />
                          {t('admin.users.ownerSuspended', 'Bloqué (principal)')}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">
                          <CheckIcon className="w-3 h-3" />
                          {t('admin.users.active')}
                        </span>
                      )}
                    </div>
                    
                    {/* Date */}
                    <div className="text-sm text-slate-500">
                      {t('admin.users.createdOn', 'Créé le')} {formatDate(secondary.createdAt)}
                    </div>
                    
                    {/* Delete action */}
                    <div className="border-t border-violet-200 pt-3">
                      {confirmDelete === secondary.id ? (
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleDelete(secondary.id)}
                            disabled={actionLoading === secondary.id}
                            className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
                          >
                            {actionLoading === secondary.id ? '...' : t('common.confirm')}
                          </button>
                          <button
                            onClick={() => setConfirmDelete(null)}
                            className="flex-1 px-4 py-2 text-sm font-medium text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200"
                          >
                            {t('common.cancel')}
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setConfirmDelete(secondary.id)}
                          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-50 text-red-700 hover:bg-red-100 rounded-lg text-sm font-medium transition-colors"
                        >
                          <TrashIcon className="w-4 h-4" />
                          {t('common.delete', 'Supprimer')}
                        </button>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          );
        })}
        
        {primaryUsers.length === 0 && (
          <div className="text-center py-12 text-slate-500">
            {t('admin.users.noUsers')}
          </div>
        )}
      </div>

      {/* Desktop: Users table */}
      <Card className="hidden md:block">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600 dark:text-slate-300 w-8"></th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600 dark:text-slate-300">
                  {t('admin.users.email')}
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600 dark:text-slate-300">
                  {t('admin.users.role')}
                </th>
                <th className="px-4 py-3 text-center text-sm font-medium text-slate-600 dark:text-slate-300">
                  {t('admin.users.accounts')}
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600 dark:text-slate-300">
                  {t('admin.users.createdAt')}
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600 dark:text-slate-300">
                  {t('admin.users.status')}
                </th>
                <th className="px-4 py-3 text-right text-sm font-medium text-slate-600 dark:text-slate-300">
                  {t('common.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
              {primaryUsers.map((user) => {
                const linkedUsers = secondaryUsersByOwner[user.id] || [];
                const hasLinkedUsers = linkedUsers.length > 0;
                const isExpanded = expandedRows.has(user.id);

                return (
                  <Fragment key={user.id}>
                    {/* Primary user row */}
                    <tr className={user.isSuspended ? 'bg-red-50 dark:bg-red-900/20' : ''}>
                      <td className="px-4 py-3">
                        {hasLinkedUsers && (
                          <button
                            onClick={() => toggleExpanded(user.id)}
                            className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded transition-colors"
                          >
                            <ChevronIcon 
                              className={`w-4 h-4 text-slate-500 dark:text-slate-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} 
                            />
                          </button>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-slate-900 dark:text-slate-100">{user.email}</p>
                          {hasLinkedUsers && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-violet-50 dark:bg-violet-900/40 text-violet-600 dark:text-violet-300 text-xs rounded-full">
                              <UsersIcon className="w-3 h-3" />
                              {linkedUsers.length}
                            </span>
                          )}
                        </div>
                        {!user.emailVerified && (
                          <span className="text-xs text-orange-600 dark:text-orange-400">
                            {t('admin.users.emailNotVerified')}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {user.role === 'admin' ? (
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getRoleBadge(user.role)}`}>
                            {t(`roles.${user.role}`)}
                          </span>
                        ) : (
                          <button
                            onClick={() => handleToggleSubscription(user)}
                            disabled={actionLoading === user.id}
                            className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full cursor-pointer hover:ring-2 hover:ring-offset-1 hover:ring-violet-400 transition-all ${getRoleBadge(user.role)} disabled:opacity-50`}
                            title={t('admin.users.toggleSubscription')}
                          >
                            {actionLoading === user.id ? (
                              <LoadingIcon className="w-3 h-3 animate-spin" />
                            ) : (
                              <SwitchIcon className="w-3 h-3" />
                            )}
                            {t(`roles.${user.role}`)}
                          </button>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center text-sm text-slate-600 dark:text-slate-300">
                        {user.accountCount}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-300">
                        {formatDate(user.createdAt)}
                      </td>
                      <td className="px-4 py-3">
                        {user.deletionRequestedAt ? (
                          <span className="inline-flex items-center gap-1 text-sm text-orange-600 dark:text-orange-400">
                            <ClockIcon className="w-4 h-4" />
                            {t('admin.users.pendingDeletion')}
                          </span>
                        ) : user.isSuspended ? (
                          <span className="inline-flex items-center gap-1 text-sm text-red-600 dark:text-red-400">
                            <BanIcon className="w-4 h-4" />
                            {t('admin.users.suspended')}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-sm text-green-600 dark:text-green-400">
                            <CheckIcon className="w-4 h-4" />
                            {t('admin.users.active')}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {renderActions(user)}
                      </td>
                    </tr>

                    {/* Secondary users rows (expanded) */}
                    {isExpanded && linkedUsers.map((secondary) => (
                      <tr key={secondary.id} className="bg-violet-50/50 dark:bg-violet-900/20">
                        <td className="px-4 py-2"></td>
                        <td className="px-4 py-2">
                          <div className="flex items-center gap-2 pl-4">
                            <LinkIcon className="w-4 h-4 text-violet-400 dark:text-violet-300" />
                            <p className="text-sm text-slate-700 dark:text-slate-200">{secondary.email}</p>
                          </div>
                        </td>
                        <td className="px-4 py-2">
                          <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300">
                            {t('admin.users.linkedAccount')}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-center text-sm text-slate-400 dark:text-slate-500">-</td>
                        <td className="px-4 py-2 text-sm text-slate-600 dark:text-slate-300">
                          {formatDate(secondary.createdAt)}
                        </td>
                        <td className="px-4 py-2">
                          {secondary.isSuspended ? (
                            <span className="inline-flex items-center gap-1 text-sm text-red-600 dark:text-red-400">
                              <BanIcon className="w-4 h-4" />
                              {t('admin.users.suspended')}
                            </span>
                          ) : secondary.ownerIsSuspended ? (
                            <span className="inline-flex items-center gap-1 text-sm text-orange-600 dark:text-orange-400">
                              <BanIcon className="w-4 h-4" />
                              {t('admin.users.ownerSuspended', 'Bloqué (principal)')}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-sm text-green-600 dark:text-green-400">
                              <CheckIcon className="w-4 h-4" />
                              {t('admin.users.active')}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2">
                          {renderActions(secondary, true)}
                        </td>
                      </tr>
                    ))}
                  </Fragment>
                );
              })}
            </tbody>
          </table>

          {primaryUsers.length === 0 && (
            <div className="text-center py-12 text-slate-500 dark:text-slate-400">
              {t('admin.users.noUsers')}
            </div>
          )}
        </div>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-slate-600 dark:text-slate-400 text-center sm:text-left">
            {t('admin.users.showing', { 
              from: (page - 1) * pageSize + 1, 
              to: Math.min(page * pageSize, total), 
              total 
            })}
          </p>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              {t('common.previous')}
            </Button>
            <span className="flex items-center px-3 text-sm text-slate-600">
              {page} / {totalPages}
            </span>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              {t('common.next')}
            </Button>
          </div>
        </div>
      )}

      {/* Role Change Confirmation Modal */}
      {confirmRoleChange && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">
              {t('admin.users.confirmRoleChange.title')}
            </h3>
            <p className="text-slate-600 dark:text-slate-400 mb-2">
              {t('admin.users.confirmRoleChange.message', { email: confirmRoleChange.email })}
            </p>
            <div className="flex items-center gap-2 mb-6 p-3 bg-slate-100 dark:bg-slate-700 rounded-lg">
              <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getRoleBadge(confirmRoleChange.role)}`}>
                {t(`roles.${confirmRoleChange.role}`)}
              </span>
              <span className="text-slate-500">→</span>
              <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getRoleBadge(confirmRoleChange.role === 'premium' ? 'free' : 'premium')}`}>
                {t(`roles.${confirmRoleChange.role === 'premium' ? 'free' : 'premium'}`)}
              </span>
            </div>
            <div className="flex justify-end gap-3">
              <Button
                variant="secondary"
                onClick={() => setConfirmRoleChange(null)}
              >
                {t('common.cancel')}
              </Button>
              <Button
                onClick={confirmToggleSubscription}
                disabled={actionLoading === confirmRoleChange.id}
              >
                {actionLoading === confirmRoleChange.id ? t('common.loading') : t('common.confirm')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Payments History Modal */}
      {viewPaymentsUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl max-w-lg w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-slate-200 dark:border-slate-700">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                  {t('admin.users.paymentsModal.title')}
                </h3>
                <button
                  onClick={() => {
                    setViewPaymentsUser(null);
                    setPaymentsData(null);
                  }}
                  className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 rounded-lg transition-colors"
                >
                  <XIcon className="w-5 h-5" />
                </button>
              </div>
              <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                {viewPaymentsUser.email}
              </p>
            </div>
            <div className="p-4 flex-1 overflow-y-auto">
              {paymentsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600"></div>
                </div>
              ) : paymentsData && paymentsData.payments.length > 0 ? (
                <div className="space-y-3">
                  {paymentsData.payments.map((payment) => (
                    <div key={payment.id} className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                          {formatDate(payment.createdAt)}
                        </span>
                        <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                          payment.status === 'completed' 
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                            : payment.status === 'pending'
                            ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                            : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                        }`}>
                          {payment.statusLabel}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-slate-600 dark:text-slate-400">
                          {payment.paymentTypeLabel}
                        </span>
                        <span className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                          {payment.amount.toFixed(2)} €
                        </span>
                      </div>
                      <div className="mt-2 text-xs text-slate-500 dark:text-slate-400 truncate" title={payment.orderId}>
                        {t('admin.users.paymentsModal.reference')}: {payment.orderId}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                  {t('admin.users.paymentsModal.noPayments')}
                </div>
              )}
            </div>
            {paymentsData && paymentsData.payments.length > 0 && (
              <div className="p-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-700/30">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600 dark:text-slate-400">
                    {t('admin.users.paymentsModal.totalPayments', { count: paymentsData.totalPayments })}
                  </span>
                  <span className="text-lg font-semibold text-violet-600 dark:text-violet-400">
                    {t('admin.users.paymentsModal.totalPaid')}: {paymentsData.totalPaid.toFixed(2)} €
                  </span>
                </div>
              </div>
            )}
            <div className="p-4 border-t border-slate-200 dark:border-slate-700 flex justify-end">
              <Button
                variant="secondary"
                onClick={() => {
                  setViewPaymentsUser(null);
                  setPaymentsData(null);
                }}
              >
                {t('common.close')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Icon components
function ChevronIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    </svg>
  );
}

function UsersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
    </svg>
  );
}

function LinkIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
    </svg>
  );
}

function BanIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function ClockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function LockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
    </svg>
  );
}

function UnlockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 10.5V6.75a4.5 4.5 0 119 0v3.75M3.75 21.75h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H3.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
    </svg>
  );
}

function RefreshIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
    </svg>
  );
}

function TrashIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
    </svg>
  );
}

function LoadingIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    </svg>
  );
}

function SwitchIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
    </svg>
  );
}

function CreditCardIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z" />
    </svg>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

export default UsersTab;
